// Port of TrailNavigation/TrackStatisticsAccumulator.swift.

import type { EpochMs, TrackPoint, TrackStatistics } from '../core/models';
import { distance, isValidCoordinate } from '../core/geo';

/** `TrackStatistics.zero`. */
export function zeroTrackStatistics(): TrackStatistics {
  return { distance: 0, movingTime: 0, stoppedTime: 0, ascent: 0, descent: 0, averageMovingSpeed: 0 };
}

/**
 * Incremental statistics during recording + batch computation. Filters fixes with accuracy > 50 m and jitter
 * (movement below max(accuracy/2, 3 m)); moving if speed ≥ 0.3 m/s or displacement rate over 30 s window ≥ 0.3 m/s.
 *
 * Processing of each fix:
 * 1. Rejected outright (ignored, no time accounted): invalid coordinate, horizontal accuracy worse than `maxAccuracy`
 *    or invalid (negative), timestamp not newer than the previous fix (out-of-order or duplicate).
 * 2. The interval since the previous fix is classified moving or stopped and added to `movingTime` / `stoppedTime`.
 *    Moving means GPS speed ≥ threshold, or displacement rate ≥ threshold, where the rate is the displacement from the
 *    most recent accepted point at least 30 s older, minus the fix's horizontal accuracy, divided by the 30 s window.
 *    After a gap longer than 2 min the gap is classified by its own average displacement rate.
 * 3. Jitter filter: a fix closer than `max(accuracy / 2, 3 m)` to the last accepted point is not accepted (returns
 *    `false`) — its time still counts in step 2.
 * 4. Accepted fixes become the new reference point. Their distance counts only when the interval was moving, so GPS
 *    drift during a long stop never inflates the distance.
 * 5. Elevation uses a simple 5 m hysteresis (suited to raw GPS/barometric altitude) over accepted fixes with a valid
 *    vertical accuracy.
 *
 * Times in `TrackStatistics` are seconds; fix timestamps are epoch milliseconds.
 */
export class TrackStatisticsAccumulator {
  /** Time window (s) of the displacement-rate test. */
  static readonly movingWindow = 30;
  /** Minimum jitter radius (m). */
  static readonly minimumJitterDistance = 3;
  /** Intervals longer than this (s) are classified by their average rate only. */
  static readonly maximumContinuousGap = 120;
  /** GPS speeds above this (m/s) are treated as glitches for `maxSpeed`. */
  static readonly maximumPlausibleSpeed = 70;
  /** Fixes with a vertical accuracy worse than this (m) do not contribute elevation. */
  static readonly maximumVerticalAccuracy = 100;

  /** Hysteresis threshold for ascent/descent (m). */
  readonly elevationThreshold: number;
  /** Speed (m/s) at or above which the hiker counts as moving. */
  readonly movingSpeedThreshold: number;
  /** Fixes with a worse horizontal accuracy (m) are ignored. */
  readonly maxAccuracy: number;

  private stats: TrackStatistics = zeroTrackStatistics();
  private accepted: TrackPoint[] = [];
  private lastSeen: TrackPoint | undefined;
  private elevationReference: number | undefined;
  /** Index of the first accepted point of the current segment (moving-window lookups never reach before it). */
  private segmentStartIndex = 0;

  /** Creates an empty accumulator. */
  constructor(elevationThreshold = 5, movingSpeedThreshold = 0.3, maxAccuracy = 50) {
    this.elevationThreshold = Number.isFinite(elevationThreshold) ? Math.max(0, elevationThreshold) : 5;
    this.movingSpeedThreshold = Number.isFinite(movingSpeedThreshold) ? Math.max(0, movingSpeedThreshold) : 0.3;
    this.maxAccuracy = Number.isFinite(maxAccuracy) ? Math.max(0, maxAccuracy) : 50;
  }

  /** Statistics so far (a copy). */
  get statistics(): TrackStatistics {
    return { ...this.stats };
  }

  /** Fixes that passed all filters, in time order (the cleaned track). */
  get acceptedPoints(): readonly TrackPoint[] {
    return this.accepted;
  }

  /** Adds a fix. Returns whether it was accepted into `acceptedPoints`. */
  append(point: TrackPoint): boolean {
    if (!isValidCoordinate(point.coordinate) || !Number.isFinite(point.timestamp)) return false;
    const accuracy = point.horizontalAccuracy;
    if (accuracy !== undefined && !(Number.isFinite(accuracy) && accuracy >= 0 && accuracy <= this.maxAccuracy)) {
      return false;
    }
    const seen = this.lastSeen;
    if (seen && point.timestamp <= seen.timestamp) return false;

    this.recordSpeed(point);
    const anchor = this.accepted[this.accepted.length - 1];
    if (!seen || !(this.accepted.length > this.segmentStartIndex) || !anchor) {
      this.lastSeen = point;
      this.accept(point);
      return true;
    }

    const interval = (point.timestamp - seen.timestamp) / 1000;
    const moving = this.isMoving(point, seen, interval);
    if (moving) this.stats.movingTime += interval;
    else this.stats.stoppedTime += interval;
    this.lastSeen = point;

    const displacement = distance(anchor.coordinate, point.coordinate);
    const jitterRadius = Math.max(
      (point.horizontalAccuracy ?? 0) / 2,
      TrackStatisticsAccumulator.minimumJitterDistance,
    );
    if (!(displacement >= jitterRadius)) {
      this.updateAverageSpeed();
      return false;
    }
    if (moving) this.stats.distance += displacement;
    this.accept(point);
    return true;
  }

  /**
   * Starts a new segment (e.g. after a paused recording is resumed): the gap is counted neither as moving nor as
   * stopped time, the jump to the next fix adds no distance, and elevation is re-anchored. Accepted points and
   * statistics so far are kept.
   */
  startNewSegment(): void {
    this.lastSeen = undefined;
    this.elevationReference = undefined;
    this.segmentStartIndex = this.accepted.length;
  }

  /**
   * Batch computation with the default parameters (same semantics as appending one by one; points are processed in
   * the given order, so out-of-order points are ignored).
   */
  static statistics(points: readonly TrackPoint[]): TrackStatistics {
    const accumulator = new TrackStatisticsAccumulator();
    for (const point of points) accumulator.append(point);
    return accumulator.statistics;
  }

  // MARK: Private

  private accept(point: TrackPoint): void {
    this.accepted.push(point);
    this.recordElevation(point);
    this.updateAverageSpeed();
  }

  private isMoving(point: TrackPoint, previous: TrackPoint, interval: number): boolean {
    if (interval > TrackStatisticsAccumulator.maximumContinuousGap) {
      return distance(previous.coordinate, point.coordinate) / interval >= this.movingSpeedThreshold;
    }
    const speed = point.speed;
    if (
      speed !== undefined &&
      Number.isFinite(speed) &&
      speed >= 0 &&
      speed <= TrackStatisticsAccumulator.maximumPlausibleSpeed &&
      speed >= this.movingSpeedThreshold
    ) {
      return true;
    }
    const reference = this.windowReference(point.timestamp);
    if (!reference) return false;
    // No accepted point exists between the reference and `now - window`, so the hiker stayed within the jitter radius
    // of the reference until then: the displacement happened within the last window, and dividing by the window (not
    // the full span) avoids under-reporting the first half-minute of walking after a long stop. The fix's own accuracy
    // is subtracted so that position wander of a stationary receiver is not mistaken for walking.
    const displacement = distance(reference.coordinate, point.coordinate) - (point.horizontalAccuracy ?? 0);
    return Math.max(0, displacement) / TrackStatisticsAccumulator.movingWindow >= this.movingSpeedThreshold;
  }

  /** Most recent accepted point at least `movingWindow` older than `date`, else the first accepted point. */
  private windowReference(date: EpochMs): TrackPoint | undefined {
    const accepted = this.accepted;
    if (!(accepted.length > this.segmentStartIndex)) return undefined;
    const first = accepted[this.segmentStartIndex];
    const cutoff = date - TrackStatisticsAccumulator.movingWindow * 1000;
    let lo = this.segmentStartIndex;
    let hi = accepted.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (accepted[mid].timestamp <= cutoff) lo = mid + 1;
      else hi = mid;
    }
    return lo > this.segmentStartIndex ? accepted[lo - 1] : first;
  }

  private recordSpeed(point: TrackPoint): void {
    const speed = point.speed;
    if (speed === undefined || !Number.isFinite(speed) || speed < 0) return;
    if (speed > TrackStatisticsAccumulator.maximumPlausibleSpeed) return;
    this.stats.maxSpeed = Math.max(this.stats.maxSpeed ?? speed, speed);
  }

  private recordElevation(point: TrackPoint): void {
    const elevation = point.elevation;
    if (elevation === undefined || !Number.isFinite(elevation)) return;
    const vertical = point.verticalAccuracy;
    if (
      vertical !== undefined &&
      !(Number.isFinite(vertical) && vertical >= 0 && vertical <= TrackStatisticsAccumulator.maximumVerticalAccuracy)
    ) {
      return;
    }
    this.stats.minElevation = Math.min(this.stats.minElevation ?? elevation, elevation);
    this.stats.maxElevation = Math.max(this.stats.maxElevation ?? elevation, elevation);
    const reference = this.elevationReference;
    if (reference === undefined) {
      this.elevationReference = elevation;
      return;
    }
    const diff = elevation - reference;
    if (diff >= this.elevationThreshold) {
      this.stats.ascent += diff;
      this.elevationReference = elevation;
    } else if (-diff >= this.elevationThreshold) {
      this.stats.descent += -diff;
      this.elevationReference = elevation;
    }
  }

  private updateAverageSpeed(): void {
    this.stats.averageMovingSpeed = this.stats.movingTime > 0 ? this.stats.distance / this.stats.movingTime : 0;
  }
}
